import { createTheme } from '@mui/material/styles';

// ─── Raw design tokens (single source of truth) ────────────────────────────
const RAW = {
  // Brand
  // primary:   '#cf9400',
  // primaryLt: '#e8a90b',
  // primaryDk: '#c99001',
  primary: '#D92648',
  primaryLt: '#e8647d',
  primaryDk: '#971a32',
  secondary: '#78AF36',
  secondaryLt: '#baec86',
  secondaryDk: '#548621',

  // Semantic / UI
  danger: '#C62828',
  dangerLight: '#E53935',
  dangerMuted: '#FEE2E2',
  warning: '#F97316',
  info: '#0EA5E9',
  success: '#10B981',

  // Base
  black: '#000000',
  white: '#ffffff',

  // Sidebar
  sidebarBg: '#3e1a2e',
  sidebarActiveBg: '#5e2333',
  sidebarText: '#ffffff',
  sidebarTextMuted: '#ffffff',
  sidebarDivider: '#ffffff14',
  sidebarHoverBg: '#ffffff40',
  contentBg: '#fafafa',

  // Neutrals
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray600: '#4B5563',
  gray700: '#374151',
  gray900: '#111827',

  // Elevation — one soft shadow for cards, palette rows, hovers, etc.
  cardShadow: '0 1px 4px rgba(0,0,0,0.05)',
  /** Thumbnails / dense grid tiles (e.g. media library) — layered, reads on busy backgrounds */
  cardShadowItem:
    '0 1px 2px rgba(17, 24, 39, 0.08), 0 5px 16px -3px rgba(17, 24, 39, 0.14)',

  // Dashboard insight card colors
  insight: {
    verified: '#6366F1',
    roomChat: '#0EA5E9',
    roomAsk: '#F97316',
    privateMsg: '#A855F7',
    discussions: '#EC4899',
    videoMeetings: '#EF4444',
    eCards: '#10B981',
    cities: '#3B82F6',
  },
};

// ─── Theme ─────────────────────────────────────────────────────────────────
const theme = createTheme({
  // ── Breakpoints ──────────────────────────────────────────────────────────
  breakpoints: {
    values: { xs: 0, sm: 600, md: 820, lg: 1280, xl: 1920 },
  },

  // ── Shape ────────────────────────────────────────────────────────────────
  shape: { borderRadius: 4 },

  // ── Typography ───────────────────────────────────────────────────────────
  // Roboto is loaded in server/views/pages/manager.ejs
  typography: {
    fontFamily: 'Roboto',
  },

  // ── Shadows ──────────────────────────────────────────────────────────────
  shadows: Array(25).fill('none'),

  // ── Palette ──────────────────────────────────────────────────────────────
  palette: {
    common: {
      black: RAW.black,
      white: RAW.white,
    },
    primary: { main: RAW.primary, light: RAW.primaryLt, dark: RAW.primaryDk },
    secondary: {
      main: RAW.secondary,
      light: RAW.secondaryLt,
      dark: RAW.secondaryDk,
    },
    error: { main: RAW.danger },
    warning: { main: RAW.warning },
    info: { main: RAW.info },
    success: { main: RAW.success },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.54)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    background: {
      default: RAW.white,
      paper: RAW.white,
    },
    divider: RAW.gray100,

    // Custom tokens accessible via theme.palette.custom.*
    custom: {
      black: RAW.black,
      white: RAW.white,
      danger: RAW.danger,
      dangerLight: RAW.dangerLight,
      dangerMuted: RAW.dangerMuted,
      gray50: RAW.gray50,
      gray100: RAW.gray100,
      gray300: RAW.gray300,
      gray400: RAW.gray400,
      gray600: RAW.gray600,
      gray700: RAW.gray700,
      gray900: RAW.gray900,
      insight: RAW.insight,
      sidebarBg: RAW.sidebarBg,
      sidebarActiveBg: RAW.sidebarActiveBg,
      sidebarText: RAW.sidebarText,
      sidebarTextMuted: RAW.sidebarTextMuted,
      sidebarDivider: RAW.sidebarDivider,
      sidebarHoverBg: RAW.sidebarHoverBg,
      contentBg: RAW.contentBg,
      cardShadow: RAW.cardShadow,
      cardShadowItem: RAW.cardShadowItem,
    },
  },

  // ── Component overrides ──────────────────────────────────────────────────
  components: {
    // Links
    MuiLink: {
      styleOverrides: {
        root: { color: RAW.secondary, fontWeight: 700 },
      },
    },

    // Buttons — no ALL-CAPS, consistent radius
    MuiButton: {
      styleOverrides: {
        root: {
          // textTransform: 'none',
          borderRadius: 4,
          fontWeight: 600,
        },
      },
    },

    // IconButton — subtle hover
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: 9999 },
      },
    },

    // Paper / Cards
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 8 },
        outlined: {
          borderColor: RAW.gray100,
        },
      },
    },

    // Table — readable rows
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: RAW.gray50,
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: RAW.gray700,
            fontSize: '0.78rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:last-child td': { border: 0 },
          '&:hover': { backgroundColor: RAW.gray50 },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: RAW.gray100 },
      },
    },

    // Chips
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: 6 },
      },
    },

    // Tooltips — readable size
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.78rem',
          backgroundColor: RAW.gray900,
          borderRadius: 4,
        },
        arrow: { color: RAW.gray900 },
      },
    },

    // Dialogs
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 10 },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { fontWeight: 700, fontSize: '1.1rem' },
      },
    },

    // ── Form inputs ─────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontFamily: 'Roboto',
          fontSize: 16,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: RAW.primary,
          },

          //Text Areas — uniform 14px padding, no fixed height (grows by rows)
          '&.MuiInputBase-multiline': {
            padding: 10,
          },
        },
        notchedOutline: { borderColor: RAW.gray300 },

        //Text Fields — single-line only (exclude the multiline textarea so it
        // keeps the §3.3 14px padding above instead of this 23px/10px box)
        input: {
          '&:not(.MuiInputBase-inputMultiline)': {
            boxSizing: 'content-box',
            height: 23,
            padding: 10,
            fontSize: 16,
            fontFamily: 'Roboto',
          },
        },

        //Text Areas — the textarea element sits inside the root's 14px padding,
        // so it carries no padding of its own (avoids doubling it).
        inputMultiline: {
          padding: 0,
          fontSize: 16,
          fontFamily: 'Roboto',
        },

        //Inputs with Icons — text padding-left: 0
        inputAdornedStart: {
          paddingLeft: 0,
        },
      },
    },

    //Dropdowns / Selects — match the same 43px box as text inputs.
    // minHeight override suppresses MUI's own 1.4375em floor so the
    // select div shrinks to the same height as a regular <input>.
    MuiSelect: {
      styleOverrides: {
        select: {
          '&.MuiOutlinedInput-input': {
            boxSizing: 'content-box',
            minHeight: 'unset',
            height: '23px',
            paddingTop: '10px',
            paddingBottom: '10px',
            paddingLeft: '10px',
            paddingRight: '32px',
            fontSize: 16,
            fontFamily: 'Roboto',
          },
        },
      },
    },

    // Floating label — center the RESTING (un-shrunk) label in the
    // 43px field (23px content + 10px top + 10px bottom padding).
    // MUI default for outlined medium is translate(14px, 16px) for a 56px field.
    // Our field is 43px → center offset = (43 - 24) / 2 ≈ 9.5 → 10px.
    // Shrink position left at MUI's default so the notch gap aligns correctly.
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          '&:not(.MuiInputLabel-shrink)': {
            top: 0,
            transform: 'translate(14px, 10px) scale(1)',
          },
          '&.MuiInputLabel-shrink': {
            top: 0,
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
      },
    },

    // Autocomplete — match regular text field sizing (10px padding, not MUI's default 9px).
    // The inner .MuiAutocomplete-input keeps its own 7.5px vertical padding so the
    // text stays vertically centred within the tighter root box.
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          '&.MuiOutlinedInput-root': {
            padding: '10px',
            paddingRight: '39px',
          },
        },
        input: {
          '&.MuiOutlinedInput-input': {
            padding: 0,
          },
        },
      },
    },

    // Inputs with Icons — icon size 20px, icon padding-left 10px
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root, & .material-icons': { fontSize: 20 },
        },
        positionStart: { marginLeft: 10 },
      },
    },

    // Tabs
    MuiTab: {
      styleOverrides: {
        root: {
          color: RAW.primary,
          // textTransform: 'none',
          fontWeight: 600,
          minWidth: 110,
        },
      },
    },

    // Sidebar / Drawer
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: `1px solid ${RAW.gray100}` },
      },
    },

    // Divider
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: RAW.gray100 },
      },
    },
  },
});

export default theme;
